import React, { useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useProducts } from '../hooks/useProducts';
import ProductForm from './ProductForm';
import PortfolioForm from './PortfolioForm';
import PortfolioEditForm from './PortfolioEditForm';
import UploadDropzone from './UploadDropzone';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import ProductEditForm from './ProductEditForm';
import CategoryEditForm from './CategoryEditForm';
import { exampleCategories, exampleProducts } from '../data/exampleProducts';
import { storage } from '../firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { CLOUDINARY_CLOUD_NAME, CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_FOLDER } from '../config';

// Projetos de exemplo para importação
const examplePortfolioProjects = [
  { 
    title: 'Sala de Estar Aconchegante', 
    category: 'Residencial', 
    type: 'Piso Vinílico',
    description: 'Sala de estar ampla com piso vinílico amadeirado, trazendo aconchego e sofisticação.',
    long_description: 'O desafio deste projeto era criar um ambiente moderno e ao mesmo tempo acolhedor para uma família. A solução foi o piso vinílico amadeirado, que além da beleza, oferece conforto térmico e acústico, perfeito para crianças e pets.',
    materials: 'Piso Vinílico LVT Eucafloor',
    gallery: [
      'https://images.unsplash.com/photo-1616046229478-9901c5536a45?auto=format&fit=crop&w=1170&q=80',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+1',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+2',
    ]
  },
  { 
    title: 'Escritório Corporativo Moderno', 
    category: 'Comercial', 
    type: 'Piso Laminado',
    description: 'Escritório moderno com piso laminado de alta resistência, ideal para áreas de grande circulação.',
    long_description: 'Neste projeto para um escritório com grande circulação de pessoas, a escolha pelo piso laminado AC4 foi ideal. Ele garante durabilidade sem abrir mão da estética, criando uma atmosfera produtiva e elegante.',
    materials: 'Piso Laminado Durafloor AC4',
    gallery: [
      'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1332&q=80',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+Escritório+1',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+Escritório+2',
    ]
  },
  { 
    title: 'Quarto Confortável', 
    category: 'Residencial', 
    type: 'Carpete',
    description: 'Quarto de casal com carpete de toque macio, garantindo conforto térmico e acústico.',
    long_description: 'Para este quarto de casal, a prioridade era o conforto. O carpete de nylon com textura plush foi a escolha perfeita, proporcionando um toque macio, isolamento acústico e um ambiente mais quente e convidativo.',
    materials: 'Carpete Residencial Belmont',
    gallery: [
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Quarto+com+Carpete',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+Carpete+1',
      'https://placehold.co/1170x800/F5F3F0/4A4A4A?text=Detalhe+Carpete+2',
    ]
  }
];

const Tabs = {
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  PORTFOLIO: 'portfolio',
};

const AdminPanel = () => {
  const { logout, user } = useAuth();
  const {
    products,
    categories,
    portfolio,
    loading,
    error,
    addProduct,
    updateProduct,
    deleteProduct,
    addCategory,
    updateCategory,
    deleteCategory,
    addPortfolioProject,
    updatePortfolioProject,
    deletePortfolioProject,
  } = useProducts();

  const [active, setActive] = useState(Tabs.PRODUCTS);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [showPortfolioEditModal, setShowPortfolioEditModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[(c.slug || c.id)] = c.title || c.name; });
    return map;
  }, [categories]);

  const handleSaveProduct = async (data) => {
    setSubmitting(true);
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
      } else {
        await addProduct(data);
      }
      setEditingProduct(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePortfolio = async (data) => {
    setSubmitting(true);
    try {
      await addPortfolioProject(data);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPortfolio = (project) => {
    setEditingPortfolio(project);
    setShowPortfolioEditModal(true);
  };

  const handleSavePortfolioEdit = async (id, data) => {
    try {
      setSubmitting(true);
      await updatePortfolioProject(id, data);
      setShowPortfolioEditModal(false);
      setEditingPortfolio(null);
    } catch (error) {
      console.error('Erro ao atualizar projeto:', error);
      alert('Erro ao atualizar projeto: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePortfolioEdit = () => {
    setShowPortfolioEditModal(false);
    setEditingPortfolio(null);
  };

  const [newCategory, setNewCategory] = useState({ title: '', slug: '', subtitle: '', description: '', benefits: '', idealFor: '', image: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ type: null, id: null, open: false });

  // Upload de imagem da categoria
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [categoryUploadProgress, setCategoryUploadProgress] = useState(0);

  const uploadCategoryImageIfNeeded = async (slug) => {
    if (!categoryImageFile) return null;
    const ext = (categoryImageFile.name.split('.').pop() || 'jpg').toLowerCase();
    const allowed = ['png','jpg','jpeg','webp','avif'];
    if (!allowed.includes(ext)) {
      throw new Error('Formato de imagem não suportado. Use png, jpg, jpeg, webp ou avif.');
    }

    // Preferir Cloudinary se configurado
    if (CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET) {
      const form = new FormData();
      form.append('file', categoryImageFile);
      form.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      if (CLOUDINARY_FOLDER) form.append('folder', CLOUDINARY_FOLDER);
      form.append('public_id', `${slug}-${Date.now()}`);

      const endpoints = [
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/auto/upload`,
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
      ];

      let lastErr;
      for (const url of endpoints) {
        try {
          const res = await fetch(url, { method: 'POST', body: form });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            lastErr = new Error(data?.error?.message || 'Falha ao enviar imagem (Cloudinary)');
            continue;
          }
          if (data && (data.secure_url || data.url)) {
            return data.secure_url || data.url;
          }
          lastErr = new Error('Resposta inesperada do Cloudinary');
        } catch (e) {
          lastErr = e;
        }
      }
      throw lastErr || new Error('Falha ao enviar imagem (Cloudinary)');
    }

    // Fallback: Firebase Storage
    const path = `categories/${slug}-${Date.now()}.${ext}`;
    const ref = storageRef(storage, path);
    const task = uploadBytesResumable(ref, categoryImageFile, { contentType: categoryImageFile.type });
    return new Promise((resolve, reject) => {
      task.on('state_changed', (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setCategoryUploadProgress(pct);
      }, reject, async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        resolve(url);
      });
    });
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.title || !newCategory.slug) return;

    setSubmitting(true);
    try {
      const payloadBase = {
        title: newCategory.title,
        slug: newCategory.slug,
        subtitle: newCategory.subtitle || '',
        description: newCategory.description || '',
        benefits: newCategory.benefits ? newCategory.benefits.split(',').map((s)=>s.trim()).filter(Boolean) : [],
        idealFor: newCategory.idealFor || '',
        image: newCategory.image || '',
      };

      let imageUrl = null;
      if (categoryImageFile) {
        imageUrl = await uploadCategoryImageIfNeeded(payloadBase.slug);
      }

      const payload = {
        ...payloadBase,
        image: imageUrl || payloadBase.image,
      };

      await (editingCategory
        ? updateCategory(editingCategory.id, payload)
        : addCategory(payload));

      setNewCategory({ title: '', slug: '', subtitle: '', description: '', benefits: '', idealFor: '', image: '' });
      setEditingCategory(null);
      setCategoryImageFile(null);
      setCategoryUploadProgress(0);
    } catch (err) {
      alert(err.message || 'Falha ao salvar categoria');
    } finally {
      setSubmitting(false);
    }
  };

  const seedExamples = async () => {
    setSeeding(true);
    try {
      const existingSlugs = new Set(categories.map((c) => c.slug || c.id));
      for (const cat of exampleCategories) {
        if (!existingSlugs.has(cat.slug)) {
          await addCategory({ title: cat.title, slug: cat.slug });
        }
      }
      const existingNames = new Set(products.map((p) => p.name));
      for (const prod of exampleProducts) {
        if (!existingNames.has(prod.name)) {
          await addProduct(prod);
        }
      }
    } finally {
      setSeeding(false);
    }
  };

  const importPortfolioExamples = async () => {
    setSeeding(true);
    try {
      const existingTitles = new Set(portfolio.map((p) => p.title));
      let importedCount = 0;
      
      for (const project of examplePortfolioProjects) {
        if (!existingTitles.has(project.title)) {
          await addPortfolioProject(project);
          importedCount++;
        }
      }
      
      if (importedCount > 0) {
        alert(`${importedCount} projetos de exemplo foram importados com sucesso!`);
      } else {
        alert('Todos os projetos de exemplo já existem no banco de dados.');
      }
    } catch (error) {
      console.error('Erro ao importar projetos:', error);
      alert('Erro ao importar projetos. Verifique o console para mais detalhes.');
    } finally {
      setSeeding(false);
    }
  };

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-[#2C5F5D]">Painel Administrativo</h1>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={logout} className="text-sm font-semibold text-white bg-[#2C5F5D] px-3 sm:px-4 py-2 rounded-lg hover:bg-[#1e4442]">Sair</button>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex gap-2">
              <button onClick={() => setActive(Tabs.PRODUCTS)} className={`px-4 py-2 rounded-t-lg border-b-0 border ${active===Tabs.PRODUCTS? 'bg-white text-[#2C5F5D] border-gray-200':'bg-gray-100 text-gray-600 border-transparent'}`}>Produtos</button>
              <button onClick={() => setActive(Tabs.CATEGORIES)} className={`px-4 py-2 rounded-t-lg border-b-0 border ${active===Tabs.CATEGORIES? 'bg-white text-[#2C5F5D] border-gray-200':'bg-gray-100 text-gray-600 border-transparent'}`}>Categorias</button>
              <button onClick={() => setActive(Tabs.PORTFOLIO)} className={`px-4 py-2 rounded-t-lg border-b-0 border ${active===Tabs.PORTFOLIO? 'bg-white text-[#2C5F5D] border-gray-200':'bg-gray-100 text-gray-600 border-transparent'}`}>Portfólio</button>
            </div>
            <div className="flex flex-col sm:flex-row sm:ml-auto gap-2">
              <input placeholder={`Buscar ${active===Tabs.PRODUCTS?'produtos':active===Tabs.CATEGORIES?'categorias':'projetos'}...`} className="w-full sm:w-56 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#D67441]" onChange={(e)=> setSearch(e.target.value)} />
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm" onChange={(e)=> setSort(e.target.value)}>
                <option value="recent">Mais recentes</option>
                <option value="az">A-Z</option>
                <option value="za">Z-A</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 bg-white border border-t-0 border-gray-200 rounded-b-lg">
        {error && <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">{error}</div>}

        {active === Tabs.PRODUCTS && (
          <div className="space-y-6 lg:space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-lg font-bold text-[#2C5F5D] mb-3">{editingProduct ? 'Editar Produto' : 'Novo Produto'}</h2>
                <ProductForm
                  initialValue={editingProduct}
                  categories={categories}
                  onSave={handleSaveProduct}
                  onCancel={() => setEditingProduct(null)}
                  submitting={submitting}
                />
              </div>
              <div className="lg:col-span-3">
                <h2 className="text-lg font-bold text-[#2C5F5D] mb-3">Produtos ({products.length})</h2>
              {loading ? (
                <p className="text-gray-500">Carregando...</p>
              ) : products.length === 0 ? (
                <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border">Nenhum produto cadastrado</div>
              ) : (
                <div className="space-y-3">
                  {products
                    .filter(p => (search||'').trim() === '' || p.name?.toLowerCase().includes(search.toLowerCase()))
                    .sort((a,b)=> sort==='az'? a.name.localeCompare(b.name) : sort==='za'? b.name.localeCompare(a.name) : (b.createdAt?.seconds||0)-(a.createdAt?.seconds||0))
                    .map((p) => (
                    <div key={p.id} className="rounded-lg border p-3 sm:p-4 flex gap-3 sm:gap-4 items-center hover:shadow-md transition-shadow">
                      <img src={p.img} alt={p.name} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base" title={p.name}>{p.name}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.active === false ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>{p.active === false ? 'Pausado' : 'Ativado'}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">{categoryMap[p.type] || p.type} • {p.brand}</p>
                        {p.thickness && <p className="text-xs text-gray-500">{p.thickness}</p>}
                        {p.feature && <p className="text-xs text-[#D67441] font-medium">{p.feature}</p>}
                      </div>
                                              <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 flex-shrink-0">
                          <button onClick={() => setEditingProduct(p)} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">Editar</button>
                          <button onClick={() => updateProduct(p.id, { active: p.active === false ? true : false })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">{p.active === false ? 'Ativar' : 'Pausar'}</button>
                          <button onClick={() => setConfirmDelete({ type: 'product', id: p.id, open: true })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors">Excluir</button>
                        </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {active === Tabs.CATEGORIES && (
          <div className="space-y-6 lg:space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-lg font-bold text-[#2C5F5D] mb-3">{editingCategory ? 'Editar Categoria' : 'Nova Categoria'}</h2>
              <form onSubmit={handleSaveCategory} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                  <input value={newCategory.title} onChange={(e)=>setNewCategory((c)=>({...c, title:e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Slug (ex.: vinilico)</label>
                  <input value={newCategory.slug} onChange={(e)=>setNewCategory((c)=>({...c, slug:e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label>
                  <input value={newCategory.subtitle} onChange={(e)=>setNewCategory((c)=>({...c, subtitle:e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea value={newCategory.description} onChange={(e)=>setNewCategory((c)=>({...c, description:e.target.value}))} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">2 Benefícios (separe por vírgulas. Ex.: "Benefício 1, Benefício 2")</label>
                  <input value={newCategory.benefits} onChange={(e)=>setNewCategory((c)=>({...c, benefits:e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ideal para</label>
                  <input value={newCategory.idealFor} onChange={(e)=>setNewCategory((c)=>({...c, idealFor:e.target.value}))} className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#D67441]" />
                </div>
                <UploadDropzone 
                  label="Imagem da Categoria" 
                  onFileSelected={setCategoryImageFile} 
                  previewUrl={categoryImageFile ? URL.createObjectURL(categoryImageFile) : newCategory.image} 
                  progress={categoryUploadProgress} 
                />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={()=>{setNewCategory({title:'', slug:'', subtitle:'', description:'', benefits:'', idealFor:'', image:''}); setEditingCategory(null); setCategoryImageFile(null); setCategoryUploadProgress(0);}} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Limpar</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-[#D67441] text-white font-bold hover:bg-[#B85A35] disabled:opacity-70">{submitting ? 'Salvando...' : 'Salvar'}</button>
                </div>
              </form>
            </div>
            <div className="lg:col-span-3">
              <h2 className="text-lg font-bold text-[#2C5F5D] mb-3">Categorias ({categories.length})</h2>
              {categories.length === 0 ? (
                <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border">Nenhuma categoria cadastrada</div>
              ) : (
                <div className="space-y-3">
                  {categories
                    .filter(c => (search||'').trim() === '' || c.title?.toLowerCase().includes(search.toLowerCase()))
                    .sort((a,b)=> sort==='az'? (a.title||'').localeCompare(b.title||'') : sort==='za'? (b.title||'').localeCompare(a.title||'') : 0)
                    .map((c) => (
                    <div key={c.id} className="rounded-lg border p-3 sm:p-4 flex gap-3 sm:gap-4 items-center hover:shadow-md transition-shadow">
                      {c.image && <img src={c.image} alt="" className="w-16 h-12 sm:w-20 sm:h-16 object-cover rounded-md border flex-shrink-0" />}
                                            <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{c.title}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.active === false ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>{c.active === false ? 'Pausada' : 'Ativada'}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-1">slug: {c.slug || c.id}</p>
                        {c.subtitle && <p className="text-xs text-gray-500">{c.subtitle}</p>}
                        {c.description && <p className="text-xs text-[#D67441] font-medium">{c.description.slice(0, 60)}{c.description.length > 60 ? '...' : ''}</p>}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 flex-shrink-0">
                        <button onClick={()=>{setEditingCategory(c)}} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">Editar</button>
                        <button onClick={()=>updateCategory(c.id, { active: c.active === false ? true : false })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">{c.active === false ? 'Ativar' : 'Pausar'}</button>
                        <button onClick={()=>setConfirmDelete({ type: 'category', id: c.id, open: true })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors">Excluir</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {active === Tabs.PORTFOLIO && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              <h2 className="text-lg font-bold text-[#2C5F5D] mb-3">Novo Projeto</h2>
              <PortfolioForm
                categories={categories}
                onSave={handleSavePortfolio}
                submitting={submitting}
              />
            </div>
            <div className="lg:col-span-3">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-bold text-[#2C5F5D]">Projetos ({portfolio.length})</h2>
                <button
                  onClick={importPortfolioExamples}
                  disabled={seeding}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {seeding ? 'Importando...' : 'Importar Exemplos'}
                </button>
              </div>
              {loading ? (
                <div className="text-center py-8 text-gray-500">Carregando projetos...</div>
              ) : portfolio.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Nenhum projeto cadastrado ainda.</div>
              ) : (
                <div className="space-y-3">
                  {portfolio
                    .filter(p => (search||'').trim() === '' || p.title?.toLowerCase().includes(search.toLowerCase()) || p.description?.toLowerCase().includes(search.toLowerCase()))
                    .sort((a,b)=> sort==='az'? (a.title||'').localeCompare(b.title||'') : sort==='za'? (b.title||'').localeCompare(a.title||'') : new Date(b.createdAt?.toDate() || 0) - new Date(a.createdAt?.toDate() || 0))
                    .map((p) => (
                    <div key={p.id} className="rounded-lg border p-3 sm:p-4 flex gap-3 sm:gap-4 items-center hover:shadow-md transition-shadow">
                      {p.gallery && p.gallery.length > 0 && (
                        <div className="flex-shrink-0 relative">
                          <img 
                            src={p.gallery[0]} 
                            alt={p.title} 
                            className="w-16 h-12 sm:w-20 sm:h-16 object-cover rounded-md border" 
                          />
                          {p.gallery.length > 1 && (
                            <span className="absolute -top-1 -right-1 bg-[#D67441] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center">
                              {p.gallery.length}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{p.title}</h3>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${p.active === false ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>{p.active === false ? 'Pausado' : 'Ativo'}</span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2">{p.description}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          {p.category && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{p.category}</span>}
                          {p.type && <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">{p.type}</span>}
                          {p.materials && <span>🔧 {p.materials}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 flex-shrink-0">
                        <button onClick={()=>handleEditPortfolio(p)} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">Editar</button>
                        <button onClick={()=>updatePortfolioProject(p.id, { active: p.active === false ? true : false })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md border border-gray-300 hover:bg-gray-50 transition-colors">{p.active === false ? 'Ativar' : 'Pausar'}</button>
                        <button onClick={()=>setConfirmDelete({ type: 'portfolio', id: p.id, open: true })} className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors">Excluir</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modais */}
      <Modal
        isOpen={!!editingProduct}
        title={editingProduct ? `Editar Produto: ${editingProduct.name}` : ''}
        onClose={() => setEditingProduct(null)}
      >
        {editingProduct && (
          <ProductEditForm
            value={editingProduct}
            categories={categories}
            submitting={submitting}
            onCancel={() => setEditingProduct(null)}
            onSave={async (data)=>{ setSubmitting(true); try { await updateProduct(editingProduct.id, data); setEditingProduct(null); } finally { setSubmitting(false); } }}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!editingCategory}
        title={editingCategory ? `Editar Categoria: ${editingCategory.title}` : ''}
        onClose={() => setEditingCategory(null)}
      >
        {editingCategory && (
          <CategoryEditForm
            value={editingCategory}
            onChangeFile={(f)=> setCategoryImageFile(f)}
            previewUrl={categoryImageFile ? URL.createObjectURL(categoryImageFile) : editingCategory.image}
            progress={categoryUploadProgress}
            submitting={submitting}
            onCancel={() => { setEditingCategory(null); setCategoryImageFile(null); setCategoryUploadProgress(0); }}
            onSubmit={async (formData)=>{
              setSubmitting(true);
              try {
                let imageUrl = null;
                if (categoryImageFile) {
                  imageUrl = await uploadCategoryImageIfNeeded(formData.slug || editingCategory.slug);
                }
                const payload = {
                  ...formData,
                  benefits: Array.isArray(formData.benefits) ? formData.benefits : (formData.benefits || '').split(',').map(s=>s.trim()).filter(Boolean),
                  image: imageUrl || formData.image || editingCategory.image || '',
                };
                await updateCategory(editingCategory.id, payload);
                setEditingCategory(null);
                setCategoryImageFile(null);
                setCategoryUploadProgress(0);
              } finally { setSubmitting(false); }
            }}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={confirmDelete.open}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita."
        onCancel={()=> setConfirmDelete({ type: null, id: null, open: false })}
        onConfirm={async ()=>{
          const { type, id } = confirmDelete;
          if (!type || !id) return;
          if (type === 'product') await deleteProduct(id);
          if (type === 'category') await deleteCategory(id);
          if (type === 'portfolio') await deletePortfolioProject(id);
          setConfirmDelete({ type: null, id: null, open: false });
        }}
        confirmText="Excluir"
      />

      {/* Modal de Edição do Portfólio */}
      {showPortfolioEditModal && editingPortfolio && (
        <PortfolioEditForm
          project={editingPortfolio}
          onSave={handleSavePortfolioEdit}
          onClose={handleClosePortfolioEdit}
          submitting={submitting}
        />
      )}
    </div>
  );
};

export default AdminPanel;
